import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, withKeys as _withKeys, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "editor-container" }
const _hoisted_2 = {
  key: 0,
  class: "editor-loading"
}
const _hoisted_3 = /*#__PURE__*/_createElementVNode("svg", { viewBox: "25 25 50 50" }, [
  /*#__PURE__*/_createElementVNode("circle", {
    cx: "50",
    cy: "50",
    r: "20"
  })
], -1)
const _hoisted_4 = [
  _hoisted_3
]

import {
  ARTICLE_CONTENT_MAX_LENGTH,
  ArticleCreateMode,
  EDITOR_ACCEPTED_FILE_EXTENSION,
  EDITOR_IMAGE_REGEX_GLOBAL,
  hexToRgb,
  rickTextEditorToolbarFunction,
  toolbarButtonTooltipMapping,
  VALIDATE_EDITOR_MAX_NUMBER_OF_IMAGE,
  VALIDATOR_IMAGE_SIZE_EXCEED,
  VALIDATOR_WRONG_FORMAT,
} from '@/views/community-admin/community-site/constants'
import { Quill, QuillEditor } from '@vueup/vue-quill'
import { computed, ComputedRef, onMounted, onUnmounted, PropType, ref } from 'vue'
import { buildFormData, buildImgUrl, editorContentPurifiedLength } from '@/utils'
import ImageUploader from 'quill-image-uploader'
import { translate } from '@/languages/i18n'
import { MAX_EDITOR_NUMBER_OF_IMAGE, MAX_UPLOAD_FILE_SIZE_BYTES, MAX_UPLOAD_FILE_SIZE_MB } from '@/constants/number'
import InputErrorMessage from '@/components/input-error-message/InputErrorMessage.vue'
import { debounce } from 'lodash'
import communitySiteAdminServices from '@/services/community-site-admin.services'
import { useRoute } from 'vue-router'
import { EDITOR_BLOCKQUOTE_DETECT, EDITOR_EMPTY_CHAR_REGEX, EDITOR_PLACEHOLDER_VISIBLE_REGEX, EMPTY_ELEMENT_REGEX } from '@/constants/regex'
import BlotFormatter from 'quill-blot-formatter'
import ResizeAction from 'quill-blot-formatter/dist/actions/ResizeAction'
import DeleteAction from 'quill-blot-formatter/dist/actions/DeleteAction'
import ImageSpec from 'quill-blot-formatter/dist/specs/ImageSpec'
import { CustomClipboard, slicedToArray, CustomImageBlot } from '@/views/community-admin/community-site/components/ArticleEditor.config'
import Delta from 'quill-delta'
import Parchment from 'parchment'
import { purifyContent } from '@/utils'


export default /*#__PURE__*/_defineComponent({
  props: {
  content: {
    type: String,
  },
  onContentChange: {
    // eslint-disable-next-line no-unused-vars
    type: Function as PropType<(p: string, mapping: any) => void>,
    default: () => null,
  },
  errors: {
    type: Array as PropType<Array<{ $validator: any; $message: any }>>,
    required: true,
  },
  onBlur: {
    type: Function as PropType<() => void>,
    default: () => null,
  },
  imageMapping: {
    type: Object as PropType<any>,
  },
  editorMode: {
    type: String as PropType<ArticleCreateMode | null>,
    default: null,
  },
},
  emits: ['triggerValidate'],
  setup(__props, { expose, emit }) {

const props = __props

Quill.register('modules/clipboard', CustomClipboard)
Quill.register(CustomImageBlot, true)

class CustomImageSpec extends ImageSpec {
  getActions() {
    return [ResizeAction, DeleteAction]
  }
}

let Font = Quill.import('attributors/class/font')
Font.whitelist = ['zenkaku', 'mplus', 'notoserif']
Quill.register(Font, true)

const route = useRoute()




const editorControl = ref()
// Use to store editor error that not insert content into editor
// which not affect form overall validation status
const editorError = ref('')
const isUploadingImage = ref<boolean>(false)
// Additional flag to distinguish between input change by select input or
// by user manual input from keyboard
const imageUploadError = ref<boolean>(false)
const blurByEditorClick = ref<boolean>(false)

const getValidator: ComputedRef<string> = computed(() => formValidator())
const getEditorContentLength: ComputedRef<{
  textContentLength: number
  contentIncludeImage: boolean
  numberOfSeparateLines: number
}> = computed(
  () =>
    editorContentPurifiedLength(props.content, imageMapping.value) ?? {
      textContentLength: 0,
      contentIncludeImage: false,
      numberOfSeparateLines: 0,
    }
)
const getEditorErrorCondition = computed(() => editorErrorCondition())
const getValidatorParams = computed(() => validatorParams())

const lastFormat = ref({})
const activeUploadCount = ref(0)
const pendingUrls = ref(new Set<string>())

const isEmptyLine = (node) => {
  return node?.innerHTML?.trim() === '<br>'
}

const onEditorReady = () => {
  const quill = editorControl.value.getQuill()
  quill.on('selection-change', (range) => {
    // resolve to keep format text when move cursor to empty line
    if (range) {
      const [line, _offset] = quill.getLine(range.index)
      // If the cursor is on a blank line, reapply lastFormat
      if (isEmptyLine(line.domNode) && lastFormat.value) {
        Object.keys(lastFormat.value).forEach((key) => {
          quill.format(key, lastFormat.value[key] ?? '')
        })
        updateFontPicker(lastFormat.value)
      }
    }
  })

  // resolve for copy image from google doc, ms word
  // handle image copy is base 64 url
  handleBase64Image(quill)
}

const handleQuillImageRendered = (event: any) => {
  const loadedSrc = event.detail?.src
  if (!loadedSrc) return

  // Check if the newly rendered URL is in the queue
  pendingUrls.value.forEach((url) => {
    if (loadedSrc.includes(url)) {
      pendingUrls.value.delete(url)
    }
  })

  checkAndCloseLoading()
}

const handleBase64Image = (quill) => {
  quill.clipboard.addMatcher('IMG', (node, delta) => {
    const src = node.getAttribute('src')

    if (!src || !src.startsWith('data:image')) {
      return delta
    }

    // Check if image is wrapped in an <a> tag and get the href
    let linkUrl = null
    let parentNode = node.parentNode
    while (parentNode && parentNode !== document.body) {
      if (parentNode.tagName === 'A' && parentNode.getAttribute('href')) {
        linkUrl = parentNode.getAttribute('href')
        break
      }
      parentNode = parentNode.parentNode
    }
    isUploadingImage.value = true
    const tempId = `uploading-${Date.now()}-${Math.random()}`
    const computed = window.getComputedStyle(node)
    const width = parseFloat(computed.width)
    const height = parseFloat(computed.height)

    processBase64Image({
      quill,
      base64: src,
      tempId,
      linkUrl,
    })

    const attributes: { width?: number; height?: number; link?: string } = {
      width,
      height,
    }

    if (linkUrl) {
      attributes.link = linkUrl
    }

    return new Delta([
      {
        insert: {
          image: tempId,
        },
        attributes,
      },
    ])
  })
}

async function processBase64Image({ quill, base64, tempId, linkUrl }) {
  try {
    const normalizedBase64 = await normalizeBase64Image(base64)
    const file = base64ToFile(normalizedBase64)

    const url = await handleUploadImage(file)
    replaceImageSrc(quill, tempId, url, linkUrl)
  } catch (err) {
    checkAndCloseLoading()
    console.error('Image paste error', err)
  }
}

function replaceImageSrc(quill, tempId, realUrl, linkUrlFromPaste) {
  const delta = quill.getContents()
  let index = 0

  delta.ops.forEach((op) => {
    if (op.insert?.image === tempId) {
      const attrs = op.attributes || {}
      // Prioritize links already in Delta (attrs.link), fallback to linkUrlFromPaste
      const linkUrl = attrs.link || linkUrlFromPaste || null

      // Delete temporary embed
      quill.deleteText(index, 1)

      // Re-insert image with actual URL
      quill.insertEmbed(index, 'image', realUrl, Quill.sources.USER)

      // Reassign the link format to the image (Quill will render <a><img/></a>)
      if (linkUrl) {
        quill.formatText(index, 1, 'link', linkUrl, Quill.sources.SILENT)
      }

      if (attrs.width) {
        const [leaf] = quill.getLeaf(index)
        const img = leaf?.domNode

        if (img instanceof HTMLImageElement) {
          img.style.width = `${attrs.width}px`
          img.style.height = 'auto'
          img.style.maxWidth = '100%'
          img.removeAttribute('width')
          img.removeAttribute('height')
        }
      }
    }

    index += typeof op.insert === 'string' ? op.insert.length : 1
  })
}

const updateFontPicker = (format) => {
  // Set selected value of font style dropdown as value of previous line
  const fontPickerContainer = document.getElementsByClassName('ql-font ql-picker')[0]
  const fPicker = fontPickerContainer?.querySelector('.ql-picker-label')
  fPicker?.setAttribute('data-value', format.font ?? '')

  // Set selected value of font size dropdown as value of previous line
  const textSizePickerContainer = document.getElementsByClassName('ql-size ql-picker')[0]
  const sPicker = textSizePickerContainer?.querySelector('.ql-picker-label')
  sPicker?.setAttribute('data-value', format.size ?? '')

  if (format?.color) {
    const rgb = hexToRgb(format.color)
    const colorPicker = document.getElementsByClassName('ql-color ql-picker ql-color-picker')[0]
    const stroke = colorPicker.querySelector('.ql-color-label.ql-stroke')
    if (stroke) {
      stroke.style = `stroke: ${rgb};`
    }
  }

  if (format?.background) {
    const rgb = hexToRgb(format.background)
    const colorPicker = document.getElementsByClassName('ql-background ql-picker ql-color-picker')[0]
    const stroke = colorPicker.querySelector('.ql-fill.ql-color-label')
    if (stroke) {
      stroke.style = `fill: ${rgb};`
    }
    const pickerLabel = colorPicker.querySelector('.ql-picker-label')
    if (!pickerLabel?.className.includes('ql-active')) {
      pickerLabel.className = `${pickerLabel.className} ql-active`
    }
  }
}

const handleEnterKeyClick = (event) => {
  if ([...event.target.classList].includes('ql-composition')) {
    return
  }
  retainFormat()
}

const retainFormat = () => {
  const format = editorControl.value.getQuill().getFormat()
  const childNodes = [...document.getElementsByClassName('ql-editor')[0].childNodes]
  childNodes.map((node) => {
    if (EMPTY_ELEMENT_REGEX.test(node.outerHTML)) {
      let outerHTML = node.outerHTML
      lastFormat.value = format
      outerHTML = outerHTML.replace('<br>', `<!--<span class='ql-font-${format.font ?? ''} ql-size-${format.size ?? ''}'><br></span>-->`)
      let spanClass = ''
      if (format?.font) {
        spanClass = `${spanClass} ql-font-${format?.font}`
      }
      if (format?.size) {
        spanClass = `${spanClass} ql-size-${format?.size}`
      }
      outerHTML = outerHTML.replace('<br>', `<span class='${spanClass}'><br></span>`)
      node.outerHTML = outerHTML
    }
    return node
  })
  updateFontPicker(format)
}

const revertToDefaultFormat = () => {
  const quill = editorControl.value.getQuill()
  const range = quill.getSelection()
  const [line] = quill.getLine(range.index)
  if (line.domNode.innerText.trim() === '') {
    updateFontPicker({ size: '', font: '' })
    quill?.format('size', '')
    quill?.format('font', '')
  } else {
    const textSizePickerContainer = document.getElementsByClassName('ql-size ql-picker')[0]
    const sSPicker = textSizePickerContainer?.querySelector('.ql-picker-label')
    const size = sSPicker?.getAttribute('data-value')

    const textFontPickerContainer = document.getElementsByClassName('ql-font ql-picker')[0]
    const sFPicker = textFontPickerContainer?.querySelector('.ql-picker-label')
    const font = sFPicker?.getAttribute('data-value')

    updateFontPicker({ size, font })
    quill?.format('size', size)
    quill?.format('font', font)
  }
}

const handleKeyDownDelete = (event) => {
  const quill = editorControl.value.getQuill()
  const range = quill.getSelection()
  if ([...event.target.classList].includes('ql-composition')) {
    return
  }

  if (range) {
    const [line, offset] = quill.getLine(range.index)
    const isEmptyLine = line.domNode.innerHTML === '<br>' || line.domNode.innerHTML.trim() === ''
    if (!isEmptyLine) {
      const childrenNode = [...line.domNode.childNodes]
      let currentLength = 0
      let size = ''
      let font = ''

      let formatNode = null
      // Traverse from left->right of that line to find the element that the cursor
      // is currently standing to retrieve style of that element
      for (let idx = 0; idx < childrenNode.length && formatNode === null; idx += 1) {
        const tmpLength = currentLength + childrenNode[idx].textContent.length
        if (tmpLength < offset) {
          currentLength += childrenNode[idx].textContent.length
        } else if (tmpLength === offset) {
          const child = childrenNode[idx + 1]
          if (idx !== childrenNode.length - 1 && ([...(child?.classList ?? [])]?.includes('ql-cursor') || [...(child?.childNodes[0]?.classList ?? [])]?.includes('ql-cursor'))) {
            formatNode = child
          }
          formatNode = childrenNode[idx]
        } else {
          formatNode = childrenNode[idx]
        }
      }

      // Retrieve style
      if (formatNode === Node.TEXT_NODE) {
        size = 'normal'
        font = ''
      } else {
        const classList = [...(formatNode?.classList ?? [])]
        classList.forEach((className) => {
          if (className.startsWith('ql-size-')) {
            size = className.replace('ql-size-', '')
          }

          if (className.startsWith('ql-font-')) {
            font = className.replace('ql-font-', '')
          }
        })
      }

      // Update UI & quill controller
      if (size === '') size = 'normal'
      const currentSize = quill.getFormat()?.size ?? 'normal'
      const currentFont = quill.getFormat()?.font ?? ''
      if (currentSize !== size) {
        quill.format('size', size)
      }
      if (currentFont !== font) {
        quill.format('font', font)
      }
      updateFontPicker({ size, font })
    }
  }
}

const editorModule = [
  {
    name: 'imageUploader',
    module: ImageUploader,
    options: {
      upload: (file) => handleUploadImage(file),
    },
  },
  {
    name: 'blotFormatter',
    module: BlotFormatter,
    options: {
      specs: [CustomImageSpec],
    },
  },
]

let imageMapping = ref({})

const compositionStartEventListener = (e) => {
  e.target.classList.add('ql-composition')
}

const compositionEndEventListener = (e) => {
  e.target.classList.remove('ql-composition')
}

const removeResizeBoxEvent = (e) => {
  if (e.target.nodeName === 'IMG' && e.target.getAttribute('src').includes('community_site')) {
    return
  }
  if (e.target.className !== 'blot-formatter__overlay' && e.target.className !== 'blot-formatter__resize-handle') {
    // Remove resize box when a resizing box is currently showing
    const resizingBox = [...document.getElementsByClassName('blot-formatter__overlay')]
    if (resizingBox.length > 0) {
      resizingBox[0].remove()
    }
  }
}

const configKeyboardBindingModule = () => {
  const quill = editorControl.value.getQuill()
  if (quill) {
    const blockquoteBackspaceRemoveQuote = {
      key: 8,
      handler: (range, context) => {
        const [line, offset] = quill.getLine(range.index)
        const textContent = line.domNode.textContent.replace(EDITOR_EMPTY_CHAR_REGEX, '')
        if (range.length === textContent.length) {
          quill.deleteText(range.index, range.length)
        }

        if (range.length !== 0) {
          return true
        }
        if (offset === 0) {
          if (textContent.length === 0) {
            line.domNode.outerHTML = '<p><br></p>'
            return false
          } else {
            const currentLineIndex = quill.getIndex(line)
            const [previousLine] = quill.getLine(currentLineIndex - 1)
            if (currentLineIndex === 0 || previousLine.domNode.nodeName !== 'BLOCKQUOTE') {
              line.domNode.outerHTML = line.domNode.outerHTML.replace(EDITOR_BLOCKQUOTE_DETECT, '<p>$1</p>')
              return false
            }
            return true
          }
        } else {
          return true
        }
      },
      format: ['blockquote'],
    }

    const customDeleteCode = {
      key: 8,
      handler: (range, context) => {
        if (range.length > 0) {
          return true
        }
        if (range.index === 0 || quill.getLength() <= 1) return
        let length = /[\uD800-\uDBFF][\uDC00-\uDFFF]$/.test(context.prefix) ? 2 : 1
        quill.deleteText(range.index - length, length, Quill.sources.USER)
      },
      format: ['code'],
    }

    const customEnterBinding = {
      key: 13,
      handler: (range, context) => {
        const [line] = quill.getLine(range.index)
        if (line.domNode.textContent.trim() === '') {
          const currentLineIndex = quill.getIndex(line)
          if (currentLineIndex === 0) {
            return true
          }

          const [previousLine] = quill.getLine(currentLineIndex - 1)
          const previousLineIndex = quill.getIndex(previousLine)
          if (previousLineIndex === 0 || previousLine.domNode.textContent.trim() !== '') {
            return true
          }

          const [popLine] = quill.getLine(previousLineIndex - 1)
          if (popLine.domNode.nodeName !== 'BLOCKQUOTE') {
            return true
          }

          line.domNode.outerHTML = '<p><br></p>'
          const previousLineLength = previousLine.length()
          quill.deleteText(previousLineIndex, previousLineLength)
          return false
        } else {
          return true
        }
      },
      format: ['blockquote'],
    }

    const handleEnter = (range, context) => {
      if (range.length > 0) {
        quill.scroll.deleteAt(range.index, range.length)
      }
      let lineFormats = Object.keys(context.format).reduce(function (lineFormats, format) {
        if (Parchment.query(format, Parchment.Scope.BLOCK) && !Array.isArray(context.format[format])) {
          lineFormats[format] = context.format[format]
        }
        return lineFormats
      }, {})

      let prevLineOuterHTML = ''
      const [line, offset] = quill.getLine(range.index)
      if (offset === 0) {
        // eslint-disable-next-line no-irregular-whitespace
        const cursorRegex = /<span class="(?:[\w-]+)">(?:&#xFEFF;|﻿)<\/span>/
        if (line.domNode.innerText.trim() === '') {
          if (line.domNode && (line.domNode.innerHTML.match(cursorRegex) || line.domNode.innerHTML.includes('<br>'))) {
            prevLineOuterHTML = line.domNode.innerHTML.replace(cursorRegex, '<br>')
          }
        } else {
          let domNode = line.domNode
          let styleNode = domNode.childNodes[0]
          // eslint-disable-next-line no-irregular-whitespace
          prevLineOuterHTML = styleNode.outerHTML?.replace(cursorRegex, '').replace(`>${styleNode.textContent.replace(/(?:&#xFEFF;|﻿)/g, '')}<`, '><br><')
        }
      }

      quill.insertText(range.index, '\n', lineFormats, Quill.sources.USER)
      quill.setSelection(range.index + 1, Quill.sources.SILENT)
      quill.focus()
      const [prevLine] = quill.getLine(range.index)
      if (prevLineOuterHTML) {
        prevLine.domNode.innerHTML = prevLineOuterHTML
      }
      Object.keys(context.format).forEach(function (name) {
        if (lineFormats[name] != null) return
        if (Array.isArray(context.format[name])) return
        if (name === 'link') return
        quill.format(name, context.format[name], Quill.sources.USER)
      })
    }

    const tabKeepFormatBinding = (range) => {
      const quill = editorControl.value.getQuill()
      quill.history.cutoff()
      const currentFormats = quill.getFormat(range.index)
      const delta = new Delta().retain(range.index).delete(range.length).insert('\t', currentFormats)
      quill.updateContents(delta, Quill.sources.USER)
      quill.history.cutoff()
      quill.setSelection(range.index + 1, Quill.sources.SILENT)
    }

    const handleBackspace = (range, context) => {
      if (range.index === 0 || quill.getLength() <= 1) return

      let getLine1 = quill.getLine(range.index),
        getLine2 = slicedToArray(getLine1, 1),
        line = getLine2[0]

      let formats = {}
      if (context.offset <= 1) {
        let getLine3 = quill.getLine(range.index - 1),
          getLine4 = slicedToArray(getLine3, 1),
          prev = getLine4[0]
        if (prev != null && prev.length() > 1) {
          let curFormats = line.formats()
          let prevFormats = quill.getFormat(range.index - 1, 1)

          formats = Delta.AttributeMap.diff(curFormats, prevFormats) || {}
        }
      }

      let length = /[\uD800-\uDBFF][\uDC00-\uDFFF]$/.test(context.prefix) ? 2 : 1
      quill.deleteText(range.index - length, length, Quill.sources.USER)

      if (context.offset === 0) {
        quill.formatLine(range.index - length, length, formats, Quill.sources.USER)
        Object.keys(formats).forEach((key) => {
          quill.format(key, formats[key], Quill.sources.USER)
        })
      } else {
        Object.keys(formats).forEach((key) => {
          quill.format(key, formats[key], Quill.sources.USER)
        })
      }
      updateFontPicker(formats)
      quill.focus()
    }

    const handleDelete = (range, context) => {
      // Check for astral symbols
      let length = /^[\uD800-\uDBFF][\uDC00-\uDFFF]/.test(context.suffix) ? 2 : 1
      if (range.index >= quill.getLength() - length) return
      let formats = {},
        nextLength = 0

      let curLine = quill.getLine(range.index),
        curLineArr = slicedToArray(curLine, 1),
        line = curLineArr[0]

      let nextLine = quill.getLine(range.index + 1),
        nextLineArr = slicedToArray(nextLine, 1),
        next = nextLineArr[0]

      if (next) {
        let curFormats = line.formats()
        let nextFormats = quill.getFormat(range.index, 1)
        formats = Delta.AttributeMap.diff(curFormats, nextFormats) || {}
        nextLength = next.length()
      }

      quill.deleteText(range.index, length, Quill.sources.USER)
      if (context.offset === 0) {
        quill.formatLine(range.index - length, length, formats, Quill.sources.USER)
        Object.keys(formats).forEach((key) => {
          quill.format(key, formats[key], Quill.sources.USER)
        })
      } else {
        if (Object.keys(formats).length > 0) {
          quill.formatLine(range.index + nextLength - 1, length, formats, Quill.sources.USER)
        }
      }
    }

    const handleEnterCode = {
      key: 13,
      handler: (range, context) => {
        let line5 = quill.getLine(range.index),
          line6 = slicedToArray(line5, 2),
          line = line6[0],
          offset = line6[1]

        let delta = new Delta()
          .retain(range.index)
          .insert('\n', context.format)
          .retain(line.length() - offset - 1)
          .retain(1, {
            code: false,
          })
        quill.updateContents(delta, Quill.sources.USER)
        quill.setSelection(range.index + 1, Quill.sources.SILENT)
        quill.scrollIntoView()
      },
      format: ['code'],
      suffix: /^$/,
      collapsed: true,
    }

    const enterAtBeginningOfLinkFix = {
      key: 13,
      collapsed: true,
      handler: (range, context) => {
        // Only handle when the cursor is inside a link
        if (!context.format?.link) return true

        const [line, offset] = quill.getLine(range.index)
        if (!line) return true

        // Only handle Enter when the cursor is at the beginning of the line
        if (offset !== 0) return true

        // Prevent Quill from cloning the <a> tag into the new empty line
        quill.insertText(range.index, '\n', Quill.sources.USER)

        // Move the cursor to the newly created empty line (above the link line)
        quill.setSelection(range.index, 0, Quill.sources.SILENT)

        // Clear link and inline formats so the empty line is not wrapped by <a>
        quill.format('link', false, Quill.sources.SILENT)
        quill.format('bold', false, Quill.sources.SILENT)
        quill.format('italic', false, Quill.sources.SILENT)

        return false // Prevent default Enter behavior
      },
    }

    const enterAtEndOfLinkFix = {
      key: 13,
      collapsed: true,
      handler(range, context) {
        // Only handle when the cursor is inside a link
        if (!context.format?.link) return true

        const [line, offset] = quill.getLine(range.index)
        if (!line) return true

        // Check if the cursor is at the end of the link line
        const isAtEnd = offset >= line.length() - 1
        if (!isAtEnd) return true

        // Let Quill insert a newline at the correct position
        quill.insertText(range.index, '\n', Quill.sources.USER)

        // Move cursor to the new line to avoid inheriting previous inline formats
        quill.setSelection(range.index + 1, 0, Quill.sources.SILENT)

        // Clear active inline formats so typing starts with a clean format state
        // (prevents font-family/font-size/color from being reset unexpectedly)
        quill.format('link', false, Quill.sources.SILENT)
        quill.format('bold', false, Quill.sources.SILENT)
        quill.format('italic', false, Quill.sources.SILENT)
        quill.format('color', false, Quill.sources.SILENT)
        quill.format('background', false, Quill.sources.SILENT)

        // return false to prevent Quill's default Enter behavior,
        // since we already manually inserted the newline above
        return false
      },
    }

    // Binding enter key
    quill.keyboard.bindings[13].unshift(enterAtBeginningOfLinkFix, enterAtEndOfLinkFix)
    quill.keyboard.bindings[13].unshift(handleEnterCode)
    quill.keyboard.bindings[13].unshift(customEnterBinding)
    quill.keyboard.bindings[13][5].handler = handleEnter

    // Binding backspace key
    quill.keyboard.bindings[8].unshift(blockquoteBackspaceRemoveQuote)
    quill.keyboard.bindings[8].unshift(customDeleteCode)
    quill.keyboard.bindings[8][5].handler = handleBackspace
    quill.keyboard.bindings[8][3].handler = handleBackspace

    // Binding Tab key
    quill.keyboard.bindings[9][5].handler = tabKeepFormatBinding

    // Binding delete key
    quill.keyboard.bindings[46][0].handler = handleDelete
  }
}

onMounted(() => {
  configEditorButtonTooltips()

  // Hide placeholder of editor when it has contents with IME input
  document.getElementsByClassName('ql-editor')[0]?.addEventListener('compositionstart', compositionStartEventListener)
  document.getElementsByClassName('ql-editor')[0]?.addEventListener('compositionend', compositionEndEventListener)
  document.addEventListener('click', removeResizeBoxEvent)
  window.addEventListener('quill-image-rendered', handleQuillImageRendered)

  configKeyboardBindingModule()
})

onUnmounted(() => {
  // Avoid memory leak
  document.getElementsByClassName('ql-editor')[0]?.removeEventListener('compositionstart', compositionStartEventListener)
  document.getElementsByClassName('ql-editor')[0]?.removeEventListener('compositionend', compositionEndEventListener)

  document.removeEventListener('click', removeResizeBoxEvent)
  window.removeEventListener('quill-image-rendered', handleQuillImageRendered)
})
/**
 * Show a little tooltip at the bottom of toolbar element when mouse hovering it
 */
const configEditorButtonTooltips = () => {
  Object.keys(toolbarButtonTooltipMapping).forEach((element) => {
    const { elementId, text, value } = toolbarButtonTooltipMapping[element]
    setButtonTooltip(elementId, text, value)
  })
}

const setButtonTooltip = (id: string, title: string, value: string) => {
  if (id.includes('ql-align-item')) {
    const align_type = id.substring(id.lastIndexOf('-') + 1)
    if (align_type === 'left') {
      document.querySelector('.ql-align .ql-picker-options span')?.setAttribute('title', translate(title))
    } else {
      document.querySelector('.ql-picker-item[data-value="' + align_type + '"]')?.setAttribute('title', translate(title))
    }
    return
  }
  const arr = document.getElementsByClassName(id)
  if (arr && arr[0] && !value) {
    arr[0].setAttribute('title', translate(title))
  } else if (value) {
    const selectedElement = [...arr].filter((e) => e.getAttribute('value') === value)
    if (selectedElement) {
      selectedElement[0].setAttribute('title', translate(title))
    }
  }
}

/**
 * Force editor to focus when the edit is clicked but current content has
 * only 1 or few lines
 */
const handleEditorClick = (event) => {
  const quill = editorControl.value.getQuill()
  const range = quill.getSelection()
  if (range) {
    const format = quill.getFormat(range.index)
    const [line, offset] = quill.getLine(range.index)
    // const isAtEndOfLine = offset >= line.length() - 1
    // if (isAtEndOfLine) {
    updateFontPicker(format)
    // }

    const isEmptyLine = line.domNode.innerHTML.trim() === '<br>'
    if (isEmptyLine) {
      const format = lastFormat.value || {}
      updateFontPicker(format)
    }
  }

  if (quill.selection.savedRange) {
    quill.selection.savedRange.index = Number.MAX_SAFE_INTEGER
    quill.selection.savedRange.length = 0
  }

  if (quill.selection.lastRange) {
    quill.selection.lastRange.index = Number.MAX_SAFE_INTEGER
    quill.selection.lastRange.length = 0
  }

  const targetElement = event.target
  if (targetElement) {
    // Temporary disable auto scroll to bottom mechanism
    // to prevent scroll when interact with selection object
    const scrollIntoView = quill.scrollIntoView
    quill.scrollIntoView = () => null

    // Handle cases when user click on editor pop-up, check if user is clicking
    // button or not
    const elementClassName = targetElement.getAttribute('class')
    if (targetElement.nodeName === 'DIV' && elementClassName.includes('editor') && !editorControl.value.getQuill().hasFocus()) {
      editorControl.value.focus()
      blurByEditorClick.value = true
      const savedRange = quill.selection.savedRange
      const { length } = savedRange
      if (length !== 0) {
        savedRange.index = length
        savedRange.length = 0
      }
      const lastRange = quill.selection.lastRange
      if (lastRange) {
        lastRange.index = length
        lastRange.length = 0
      }
    }

    // Recover scroll behavior
    quill.scrollIntoView = scrollIntoView
  }
}

const handleOnBlur = () => {
  setTimeout(() => {
    // trim content
    if (!blurByEditorClick.value) {
      if (getEditorContentLength.value.textContentLength === 0 && !getEditorContentLength.value.contentIncludeImage) {
        editorControl.value.setContents('')
      }

      // let content = editorControl.value.getContents() ?? ''
      // const { trimContentFlg, content: newContent } = editorTrimBottomOnlyNewLine(content)
      // if (trimContentFlg) {
      //   editorControl.value.setContents(newContent)
      // }
      props?.onBlur()
    }

    blurByEditorClick.value = false
  }, 0)
}

const handleOnTextChange = debounce(() => {
  if (!editorControl.value) return
  checkNumberImageValid()
  blurByEditorClick.value = false
  // Remove form error
  // Update content to parent state
  const content = editorControl.value.getContents()
  props?.onContentChange(content, imageMapping.value)
  if (!imageUploadError.value) {
    setFormError('')
  } else {
    imageUploadError.value = false
  }

  const emptyTest = EDITOR_PLACEHOLDER_VISIBLE_REGEX.filter((pattern) => {
    return pattern.test(content)
  }).length
  if (emptyTest > 0) {
    document.getElementsByClassName('ql-editor')[0]?.classList.add('ql-blank')
  }
}, 300)

const setFormError = (error: string) => {
  editorError.value = error
}

const checkAndCloseLoading = () => {
  if (activeUploadCount.value <= 0 && pendingUrls.value.size === 0) {
    activeUploadCount.value = 0
    isUploadingImage.value = false
  }
}

const handleUploadImage = (file: any) => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      checkNumberImageValid()
      // File type not supported and can't be uploaded
      if (!EDITOR_ACCEPTED_FILE_EXTENSION.includes(file.type)) {
        imageUploadError.value = true
        setFormError(VALIDATOR_WRONG_FORMAT)
        reject('')
      }
      // File size exceeded
      else if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
        imageUploadError.value = true
        setFormError(VALIDATOR_IMAGE_SIZE_EXCEED)
        reject('')
      }
      // Valid input file
      else {
        activeUploadCount.value++
        isUploadingImage.value = true
        buildImgUrl(file).then(({ newFile }) => {
          // Rename file to minimize length of html content
          const { name } = newFile
          const fileNameParts = name.split('.')
          const fileExtension = fileNameParts ? fileNameParts[fileNameParts.length - 1] : ''
          const renamedFile = new File([newFile], `${Date.now()}.${fileExtension}`, { type: newFile.type })
          const formData = new FormData()
          buildFormData(formData, {
            image: renamedFile,
          })
          communitySiteAdminServices
            .uploadImage(route.params?.id, formData)
            .then((result) => {
              if (result?.response?.status === 400 && result?.response?.data?.data[0]?.status === 400) {
                imageUploadError.value = true
                setFormError(VALIDATOR_WRONG_FORMAT)
                reject('')
              } else if (result.status === 200 && result.data.success) {
                const { temp_image_url } = result.data
                let imageUrl = new URL(result.data.image_url)
                if (props.editorMode) {
                  if (props.editorMode === ArticleCreateMode.CREATE) {
                    imageUrl.searchParams.append('post_id', '{{post_id}}')
                  } else {
                    const { idArticle } = route.params
                    imageUrl.searchParams.append('post_id', idArticle.toString())
                  }
                }
                const finalUrl = decodeURIComponent(imageUrl.toString())
                imageMapping.value[temp_image_url] = finalUrl
                pendingUrls.value.add(temp_image_url)
                resolve(temp_image_url)
              }
            })
            .catch((err) => {
              reject(err)
            })
            .finally(() => {
              activeUploadCount.value--
              checkAndCloseLoading()
            })
        })
      }
    }, 300)
  })
}

const checkNumberImageValid = () => {
  const editorContent = editorControl.value.getContents() ?? ''
  const numberOfCurrentImages = (editorContent.match(EDITOR_IMAGE_REGEX_GLOBAL) ?? []).length
  // Maximum number of image reached limit
  if (numberOfCurrentImages > MAX_EDITOR_NUMBER_OF_IMAGE) {
    imageUploadError.value = true
    setFormError(VALIDATE_EDITOR_MAX_NUMBER_OF_IMAGE)
  }
}

const formValidator = () => {
  const { errors } = props
  if (editorError.value) {
    return editorError.value
  }
  return errors[0]?.$validator === '$externalResults' ? errors[0].$message.toString() : errors[0]?.$validator
}

const editorErrorCondition = () => {
  if (editorError.value) return true
  if (!props.errors) return false
  return props.errors[0] && props.errors[0].$validator !== 'maxString'
}

const setImageMapping = (mapping) => {
  imageMapping.value = mapping
}

const setEditorContent = (content) => {
  const sanitizedContent = purifyContent(content)
  editorControl.value.setContents(sanitizedContent, Quill.sources.SILENT)
  setTimeout(() => {
    const { history } = editorControl.value.getQuill()
    history.clear()
  }, 0)
}

const validatorParams = () => {
  const validator = formValidator()
  if (validator === VALIDATOR_IMAGE_SIZE_EXCEED) {
    return MAX_UPLOAD_FILE_SIZE_MB
  }
  if (validator == 'maxString') {
    return ARTICLE_CONTENT_MAX_LENGTH
  }
}

const triggerContentValidate = () => {
  emit('triggerValidate')
}

const getContentWithFixedSizeImages = () => {
  const editor = document.getElementsByClassName('editor')[0]
  const images = editor.querySelectorAll('img:not([width])')
  images.forEach((image) => {
    image.setAttribute('width', image.width)
    image.setAttribute('height', image.height)
  })

  return editorControl.value.getContents()
}

// copy image from ms word on window return image/gif, not a permitted format
// Redraw image by canvas and export url to get valid image/png
const normalizeBase64Image = (base64, outputType = 'image/png') => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height

      const ctx = canvas.getContext('2d')
      ctx?.drawImage(img, 0, 0)

      resolve(canvas.toDataURL(outputType))
    }

    img.onerror = reject
    img.src = base64
  })
}

const base64ToFile = (base64, filename = `paste_${Date.now()}.png`) => {
  const [header, data] = base64.split(',')
  const mime = header.match(/:(.*?);/)[1]

  const binary = atob(data)
  const len = binary.length
  const buffer = new Uint8Array(len)

  for (let i = 0; i < len; i++) {
    buffer[i] = binary.charCodeAt(i)
  }

  return new File([buffer], filename, { type: mime })
}

expose({
  setImageMapping,
  setEditorContent,
  getContentWithFixedSizeImages,
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", {
      class: _normalizeClass(["content-number-of-characters d-flex align-items-center", _unref(getEditorContentLength)?.textContentLength + _unref(getEditorContentLength)?.numberOfSeparateLines - 1 > _unref(ARTICLE_CONTENT_MAX_LENGTH) ? 'text-danger fw-bold' : ''])
    }, _toDisplayString(_unref(translate)('CK20CurrentNumberOfCharacters').replace('%d', (_unref(getEditorContentLength)?.textContentLength + _unref(getEditorContentLength)?.numberOfSeparateLines - 1).toString(10))), 3),
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_unref(QuillEditor), {
        class: "editor",
        ref: (_value, _refs) => {
          _refs['editorControl'] = _value
          editorControl.value = _value
        },
        contentType: "html",
        style: {"min-height":"200px","max-height":"640px"},
        modules: editorModule,
        toolbar: _unref(rickTextEditorToolbarFunction),
        onTextChange: _unref(handleOnTextChange),
        onClick: handleEditorClick,
        onBlur: handleOnBlur,
        onKeydown: [
          _withKeys(handleEnterKeyClick, ["enter"]),
          _withKeys(handleKeyDownDelete, ["delete"])
        ],
        placeholder: _unref(translate)('SD10requireText'),
        onPaste: triggerContentValidate,
        onReady: onEditorReady
      }, null, 8, ["toolbar", "onTextChange", "onKeydown", "placeholder"]),
      (isUploadingImage.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, _hoisted_4))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", {
      id: "dummy",
      class: _normalizeClass(_unref(getEditorErrorCondition) ? 'is-invalid' : '')
    }, null, 2),
    _createVNode(InputErrorMessage, {
      title: _unref(translate)('content'),
      params: [_unref(getValidatorParams)],
      validator: _unref(getValidator)
    }, null, 8, ["title", "params", "validator"])
  ], 64))
}
}

})